import React from 'react';

import { ImageSize } from '@nxte-nl/image-hooks';

import { ProductCardDataLayerContainer } from
  '../../containers/ProductCardDataLayerContainer/ProductCardDataLayerContainer';
import { ITEM_POSITION_SHIFT } from '../../hooks/productListHooks/productListHooks';
import { textModifyHooks } from '../../hooks/textModifyHooks/textModifyHooks';
import { UrlType } from '../../types/apiEnums';
import {
  ColorPresentational,
  ImagePresentational,
  LinkPresentational,
  ProductCardType,
  UrlPresentational,
} from '../../types/common';
import { ItemListID } from '../../types/dataLayer';
import { DangerouslySetInnerHtml } from '../DangerouslySetInnerHtml/DangerouslySetInnerHtml';
import { ImageWithSize } from '../ImageWithSize/ImageWithSize';
import { LayoutWidth, LayoutWrapper } from '../LayoutWrapper/LayoutWrapper';
import { Title } from '../Title/Title';
import styles from './BlogArticleBlockItem.module.css';

export type Props = {
  coloredBlocks: ColoredBlockPresentational[] | null;
  firstDescription: string | null;
  image: ImagePresentational | null;
  isLandingPage?: boolean;
  secondDescription: string | null;
  title: string;
  withHandBlockBackgroundImage: ImagePresentational | null;
  withHandBlockCardImage: ImagePresentational | null;
  withHandBlockDescription: string | null;
  withHandBlockIsShowHand: boolean | null;
  withHandBlockIsShowShadow: boolean | null;
  withHandBlockLink: LinkPresentational | null;
  withHandBlockTitle: string | null;
};

export type ColoredBlockPresentational = {
  backgroundColor: ColorPresentational;
  description: string;
  image: ImagePresentational;
  referencedPath: string;
  referencedPath_v1: UrlPresentational;
  title: string;
};

export const BlogArticleBlockItem = React.memo((props: Props) => {
  const {
    title,
    image,
    withHandBlockTitle,
    withHandBlockLink,
    withHandBlockDescription,
    withHandBlockCardImage,
    withHandBlockIsShowHand,
    withHandBlockIsShowShadow,
    withHandBlockBackgroundImage,
    coloredBlocks,
    firstDescription,
    secondDescription,
    isLandingPage,
  } = props;

  const titleAnchorId = textModifyHooks.useGenerateAnchorId(title);

  return (
    <div className={styles.blogArticleBlockItem}>
      <LayoutWrapper size={LayoutWidth.Narrow}>
        <Title
          title={title}
          className={styles.title}
          id={titleAnchorId}
          component='h2'
          variant='h3'
        />

        <DangerouslySetInnerHtml
          variant={'body1'}
          className={styles.description}
          dangerousHtml={firstDescription ?? ''}
        />

        <DangerouslySetInnerHtml
          variant={'body1'}
          className={styles.description}
          dangerousHtml={secondDescription ?? ''}
        />

        {image && (
          <ImageWithSize
            image={image}
            size={ImageSize.XL}
            className={styles.blockImage}
          />
        )}

        {withHandBlockTitle && (
          <ProductCardDataLayerContainer
            position={0}
            title={withHandBlockLink?.title ?? withHandBlockTitle}
            path={withHandBlockLink?.url.href}
            isExsternalLink={withHandBlockLink?.url.type === UrlType.ABSOLUTE}
            description={withHandBlockDescription}
            cardImage={withHandBlockCardImage}
            backgroundImage={withHandBlockBackgroundImage}
            className={styles.blockWithHand}
            showHand={withHandBlockIsShowHand ?? false}
            backgroundColor={null}
            id={null}
            cardType={ProductCardType.L}
            isTopProduct
            showShadow={withHandBlockIsShowShadow ?? false}
          />
        )}
        {coloredBlocks && coloredBlocks.length > 0 && (
          <div className={styles.coloredBlocks}>
            {coloredBlocks.map((item, index) => (
              <ProductCardDataLayerContainer
                position={index + ITEM_POSITION_SHIFT}
                key={index}
                title={item.title}
                path={item.referencedPath_v1.href}
                isExsternalLink={item.referencedPath_v1.type === UrlType.ABSOLUTE}
                description={item.description}
                cardImage={item.image}
                backgroundColor={item.backgroundColor}
                className={styles.coloredBlockItem}
                backgroundImage={null}
                id={null}
                showHand={false}
                cardType={ProductCardType.S}
                itemListID={isLandingPage ? ItemListID.LANDING_PAGE : ItemListID.BLOG_ARTICLE_PAGE}
              />
            ))}
          </div>
        )}
      </LayoutWrapper>
    </div>
  );
});

BlogArticleBlockItem.displayName = 'BlogArticleBlockItem';
